import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  CaretDown, CaretRight, Plus, FloppyDisk, Export, ArrowClockwise,
  Diamond, Warning, Eye, EyeSlash, ChatCircle, X, Trash, ArrowsOutLineHorizontal,
  CalendarBlank, Users as UsersIcon, Clock, CheckCircle, ClockCounterClockwise
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Sparkle } from '@phosphor-icons/react';
import GeneratePlanningIADialog from './GeneratePlanningIADialog';
import ElementsTerrainSwimlane from './ElementsTerrainSwimlane';
import PlanningVersionsModal from './PlanningVersionsModal';
import usePermissions from '../../hooks/usePermissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== DATE UTILS (FR working days) =====
const FR_HOLIDAYS_2026 = new Set([
  '2026-01-01','2026-04-06','2026-05-01','2026-05-08','2026-05-14',
  '2026-05-25','2026-07-14','2026-08-15','2026-11-01','2026-11-11','2026-12-25'
]);

function isWorkDay(d) {
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const iso = d.toISOString().slice(0, 10);
  return !FR_HOLIDAYS_2026.has(iso);
}

function addWorkDays(startStr, days) {
  if (!startStr || days <= 0) return startStr;
  const d = new Date(startStr + 'T00:00:00');
  let added = 0;
  while (added < days) {
    if (isWorkDay(d)) added++;
    if (added < days) d.setDate(d.getDate() + 1);
  }
  return d.toISOString().slice(0, 10);
}

function daysBetween(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

function formatDateFR(str) {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ===== VIEW MODES =====
const VIEW_MODES = {
  week: { label: 'Semaine', dayWidth: 36, headerFormat: 'week' },
  month: { label: 'Mois', dayWidth: 12, headerFormat: 'month' },
  quarter: { label: 'Trimestre', dayWidth: 4, headerFormat: 'quarter' },
};

// ===== COLORS =====
const COLORS = {
  progress: '#1E3A5F',
  progressBg: '#CBD5E1',
  critical: '#C75B39',
  criticalBg: '#FFD7CC',
  baseline: '#D4D4D4',
  milestone: '#D4A84B',
  group: '#475569',
  today: '#EF4444',
  gridLine: '#F0F0EE',
  gridWeekend: '#FAFAF9',
};

// Row height and header height
const ROW_H = 40;
const HEADER_H = 52;
// Left-panel column layout (sticky left)
const COL_CHECK = 28;
const COL_NAME = 210;
const COL_LOT = 96;
const COL_ENT = 150;
const COL_DUR = 52;
const COL_PCT = 92;
const COL_ACTIONS = 28;
const LEFT_W = COL_CHECK + COL_NAME + COL_LOT + COL_ENT + COL_DUR + COL_PCT + COL_ACTIONS; // ~656

export default function GanttPlanning({ chantierId, userRole }) {
  const { perms } = usePermissions(chantierId);
  const canModifyPlanning = perms?.can_modify_planning;
  const planTitle = canModifyPlanning ? undefined : "Droit « Modifier le planning » requis (selon votre rôle ou droit confié). Demandez à l'architecte.";
  const [planning, setPlanning] = useState(null);
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [collapsed, setCollapsed] = useState({});
  const [editTask, setEditTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [baselineOpen, setBaselineOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [baselineLabel, setBaselineLabel] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishMsg, setPublishMsg] = useState('');
  const [alertes, setAlertes] = useState([]);
  const [alertesOpen, setAlertesOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTaskParent, setNewTaskParent] = useState('');
  const [newTaskData, setNewTaskData] = useState({ nom: '', type: 'TACHE', entreprise_nom: '', lot_marche_code: '', date_debut: '', duree_jours: 5, notes: '' });
  const [liens, setLiens] = useState([]);
  const [lotsMarches, setLotsMarches] = useState([]);
  const [dragState, setDragState] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [showIAGenerate, setShowIAGenerate] = useState(false);
  const [cascadedIds, setCascadedIds] = useState(new Set());
  // Iter 91 J3 — Filtre "ignored" : masquées par défaut, toggle non persisté
  const [showIgnored, setShowIgnored] = useState(false);
  const svgRef = useRef(null);
  const scrollRef = useRef(null);
  const importInputRef = useRef(null);

  // iter63f-bis : autorise édition selon perms.can_modify_planning (incluant overrides),
  // avec fallback sur userRole admin/architect en chargement
  const canEdit = canModifyPlanning ?? ['admin', 'architect', 'collaborateur'].includes(userRole);
  const isClient = userRole === 'client';

  // ===== FETCH =====
  const fetchPlanning = useCallback(async () => {
    try {
      const [planRes, alertesRes, liensRes, marchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/planning`, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/alertes-crc`, { withCredentials: true }).catch(() => ({ data: { alertes: [] } })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/liens-crc`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPlanning(planRes.data);
      setTaches(planRes.data?.taches || []);
      setAlertes(alertesRes.data?.alertes || []);
      setLiens(liensRes.data || []);
      setLotsMarches(Array.isArray(marchesRes.data) ? marchesRes.data : []);
    } catch { /* empty */ } finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { fetchPlanning(); }, [fetchPlanning]);

  // ===== VISIBLE TACHES (filtered by collapse + Iter 91 J3 ignored filter) =====
  // Iter 91 J3 — Filtre ignored : par défaut masque ignored=true, toggle "Voir les ignorées" pour afficher.
  // Filtre `!== true` pour englober legacy `undefined` (90 docs pré-Iter91) ET `false` explicite.
  const ignoredCount = useMemo(
    () => taches.filter(t => t.ignored === true).length,
    [taches]
  );

  const visibleTaches = useMemo(() => {
    const baseTaches = showIgnored ? taches : taches.filter(t => t.ignored !== true);
    const result = [];
    for (const t of baseTaches) {
      if (!t.parent_id) {
        result.push(t);
        if (t.type === 'GROUPE' && !collapsed[t.id]) {
          const children = baseTaches.filter(c => c.parent_id === t.id).sort((a, b) => a.ordre - b.ordre);
          result.push(...children);
        }
      }
    }
    return result;
  }, [taches, collapsed, showIgnored]);

  // ===== TIMELINE RANGE =====
  const { timelineStart, timelineDays, todayOffset } = useMemo(() => {
    if (taches.length === 0) {
      const s = new Date(); s.setDate(1); s.setMonth(s.getMonth() - 1);
      return { timelineStart: s.toISOString().slice(0, 10), timelineDays: 365, todayOffset: 30 * VIEW_MODES[viewMode].dayWidth };
    }
    let minDate = '2999-12-31', maxDate = '2000-01-01';
    for (const t of taches) {
      if (t.date_debut && t.date_debut < minDate) minDate = t.date_debut;
      if (t.date_debut_baseline && t.date_debut_baseline < minDate) minDate = t.date_debut_baseline;
      const end = t.date_fin_calculee || t.date_debut;
      if (end && end > maxDate) maxDate = end;
      if (t.date_debut_baseline && t.duree_baseline) {
        const blEnd = addWorkDays(t.date_debut_baseline, t.duree_baseline);
        if (blEnd > maxDate) maxDate = blEnd;
      }
    }
    // Pad 30 days on each side
    const s = new Date(minDate + 'T00:00:00'); s.setDate(s.getDate() - 30);
    const e = new Date(maxDate + 'T00:00:00'); e.setDate(e.getDate() + 60);
    const start = s.toISOString().slice(0, 10);
    const days = daysBetween(start, e.toISOString().slice(0, 10));
    const today = new Date().toISOString().slice(0, 10);
    const tOff = daysBetween(start, today) * VIEW_MODES[viewMode].dayWidth;
    return { timelineStart: start, timelineDays: days, todayOffset: tOff };
  }, [taches, viewMode]);

  const dayWidth = VIEW_MODES[viewMode].dayWidth;
  const totalWidth = timelineDays * dayWidth;

  // ===== HELPERS =====
  const getBarX = (dateStr) => {
    if (!dateStr) return 0;
    return daysBetween(timelineStart, dateStr) * dayWidth;
  };
  const getBarWidth = (startStr, endStr) => {
    if (!startStr || !endStr) return dayWidth;
    return Math.max(daysBetween(startStr, endStr) * dayWidth, dayWidth);
  };

  // ===== ACTIONS =====
  const handleCreatePlanning = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning`, {}, { withCredentials: true });
      toast.success('Planning créé');
      fetchPlanning();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const showCascadeFeedback = useCallback((cascaded) => {
    if (!Array.isArray(cascaded) || cascaded.length === 0) return;
    const ids = cascaded.map(c => c.id);
    setCascadedIds(new Set(ids));
    const lines = cascaded.slice(0, 4).map(c => `• ${c.nom} → ${formatDateFR(c.nouvelle_date_debut)}`);
    const extra = cascaded.length > 4 ? `\n… et ${cascaded.length - 4} autre(s)` : '';
    toast.info(`${cascaded.length} tâche${cascaded.length > 1 ? 's' : ''} décalée${cascaded.length > 1 ? 's' : ''} automatiquement`, {
      description: lines.join('\n') + extra,
      duration: 6000,
    });
    setTimeout(() => setCascadedIds(new Set()), 6000);
  }, []);

  const handleSaveTask = async (taskData) => {
    try {
      let resp;
      if (taskData.id && taches.find(t => t.id === taskData.id)) {
        resp = await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${taskData.id}`, taskData, { withCredentials: true });
      } else {
        resp = await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning/taches`, taskData, { withCredentials: true });
      }
      setEditOpen(false);
      await fetchPlanning();
      showCascadeFeedback(resp?.data?.cascaded);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur sauvegarde'); }
  };

  const handleDeleteTask = async (tId) => {
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${tId}`, { withCredentials: true });
      setEditOpen(false);
      fetchPlanning();
      toast.success('Tâche supprimée');
    } catch (err) { toast.error('Erreur suppression'); }
  };

  const handleSaveBaseline = async () => {
    if (!baselineLabel.trim()) { toast.error('Label requis'); return; }
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning/baseline`, { label: baselineLabel }, { withCredentials: true });
      toast.success('Baseline sauvegardée');
      setBaselineOpen(false);
      setBaselineLabel('');
      fetchPlanning();
    } catch (err) { toast.error('Erreur baseline'); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning/import`, fd, { withCredentials: true });
      const parts = [];
      if (data.added) parts.push(`${data.added} ajoutée(s)`);
      if (data.ignored) parts.push(`${data.ignored} doublon(s) ignoré(s)`);
      if (data.errors?.length) parts.push(`${data.errors.length} erreur(s)`);
      toast.success(parts.join(' · ') || data.message || 'Import terminé', {
        description: data.errors?.length ? data.errors.slice(0, 3).join(' | ') : undefined,
        duration: 6000,
      });
      fetchPlanning();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur import'); }
    e.target.value = '';
  };

  // ===== INLINE EDIT (left table) =====
  const patchTask = async (taskId, patch) => {
    // Optimistic update
    setTaches(prev => prev.map(t => (t.id === taskId ? { ...t, ...patch } : t)));
    try {
      const resp = await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${taskId}`, patch, { withCredentials: true });
      showCascadeFeedback(resp?.data?.cascaded);
      if (resp?.data?.cascaded?.length) fetchPlanning();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur mise à jour');
      fetchPlanning();
    }
  };

  const openTaskDetail = (task) => {
    setDetailTaskId(task.id);
    setDetailTask({ ...task });
  };


  const handlePublish = async () => {
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning/publier`, { message_client: publishMsg }, { withCredentials: true });
      toast.success('Planning publié pour le client');
      setPublishOpen(false);
      setPublishMsg('');
    } catch (err) { toast.error('Erreur publication'); }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    await handleSaveTask({ nom: newGroupName, type: 'GROUPE', date_debut: new Date().toISOString().slice(0, 10), duree_jours: 1, ordre: taches.length });
    setNewGroupOpen(false);
    setNewGroupName('');
  };

  const handleAddTask = async () => {
    if (!newTaskData.nom.trim()) return;
    await handleSaveTask({
      ...newTaskData,
      parent_id: newTaskParent || null,
      lot: taches.find(t => t.id === newTaskParent)?.nom || '',
      ordre: taches.length,
    });
    setNewTaskOpen(false);
    setNewTaskData({ nom: '', type: 'TACHE', entreprise_nom: '', lot_marche_code: '', date_debut: '', duree_jours: 5, notes: '' });
  };

  // ===== DRAG & DROP =====
  const handleMouseDown = (e, task, mode) => {
    if (!canEdit) return;
    e.stopPropagation();
    const svgRect = svgRef.current?.getBoundingClientRect();
    const scrollLeft = scrollRef.current?.scrollLeft || 0;
    setDragState({
      taskId: task.id,
      mode, // 'move' or 'resize'
      startX: e.clientX,
      origDateDebut: task.date_debut,
      origDuree: task.duree_jours,
      svgLeft: svgRect?.left || 0,
      scrollLeft,
    });
  };

  useEffect(() => {
    if (!dragState) return;
    const handleMouseMove = (e) => {
      const dx = e.clientX - dragState.startX;
      const daysDelta = Math.round(dx / dayWidth);
      if (daysDelta === 0) return;
      setTaches(prev => prev.map(t => {
        if (t.id !== dragState.taskId) return t;
        if (dragState.mode === 'move') {
          const origDate = new Date(dragState.origDateDebut + 'T00:00:00');
          origDate.setDate(origDate.getDate() + daysDelta);
          const newStart = origDate.toISOString().slice(0, 10);
          return { ...t, date_debut: newStart, date_fin_calculee: addWorkDays(newStart, t.duree_jours) };
        } else {
          const newDuree = Math.max(1, dragState.origDuree + daysDelta);
          return { ...t, duree_jours: newDuree, date_fin_calculee: addWorkDays(t.date_debut, newDuree) };
        }
      }));
    };
    const handleMouseUp = async () => {
      if (!dragState) return;
      const task = taches.find(t => t.id === dragState.taskId);
      if (task) {
        try {
          const resp = await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${task.id}`,
            { date_debut: task.date_debut, duree_jours: task.duree_jours },
            { withCredentials: true }
          );
          showCascadeFeedback(resp?.data?.cascaded);
        } catch { /* ignore */ }
      }
      setDragState(null);
      fetchPlanning();
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [dragState, dayWidth, taches, chantierId, fetchPlanning, showCascadeFeedback]);

  // ===== TIMELINE HEADER (sticky top, HEADER_H tall only) =====
  const renderTimelineHeader = () => {
    const items = [];
    const start = new Date(timelineStart + 'T00:00:00');
    if (viewMode === 'week') {
      for (let i = 0; i < timelineDays; i++) {
        const d = new Date(start); d.setDate(d.getDate() + i);
        const x = i * dayWidth;
        if (d.getDay() === 1 || i === 0) {
          items.push(<text key={`wh-${i}`} x={x + 2} y={16} className="text-[9px] fill-[#6B6B6B]" style={{ fontSize: 9 }}>
            S{Math.ceil((i + 1) / 7)}
          </text>);
        }
        items.push(<text key={`d-${i}`} x={x + dayWidth / 2} y={42} textAnchor="middle" className="fill-[#9A9A9A]" style={{ fontSize: 8 }}>
          {d.getDate()}
        </text>);
      }
    } else if (viewMode === 'month') {
      let lastMonth = -1;
      for (let i = 0; i < timelineDays; i++) {
        const d = new Date(start); d.setDate(d.getDate() + i);
        const month = d.getMonth();
        const x = i * dayWidth;
        if (month !== lastMonth) {
          items.push(<text key={`mh-${i}`} x={x + 4} y={16} className="fill-[#2C2C2C]" style={{ fontSize: 11, fontWeight: 600 }}>
            {d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </text>);
          if (lastMonth >= 0) items.push(<line key={`ml-${i}`} x1={x} y1={0} x2={x} y2={HEADER_H} stroke="#E0E0DB" strokeWidth={1} />);
          lastMonth = month;
        }
        if (d.getDate() === 1 || d.getDate() === 15) {
          items.push(<text key={`md-${i}`} x={x} y={42} className="fill-[#9A9A9A]" style={{ fontSize: 8 }}>
            {d.getDate()}
          </text>);
        }
      }
    } else {
      let lastQ = -1;
      for (let i = 0; i < timelineDays; i++) {
        const d = new Date(start); d.setDate(d.getDate() + i);
        const q = Math.floor(d.getMonth() / 3);
        const x = i * dayWidth;
        if (q !== lastQ || (d.getDate() === 1 && d.getMonth() % 3 === 0)) {
          items.push(<text key={`qh-${i}`} x={x + 4} y={16} className="fill-[#2C2C2C]" style={{ fontSize: 11, fontWeight: 600 }}>
            T{q + 1} {d.getFullYear()}
          </text>);
          lastQ = q;
        }
        if (d.getDate() === 1) {
          items.push(<text key={`qm-${i}`} x={x} y={42} className="fill-[#9A9A9A]" style={{ fontSize: 7 }}>
            {d.toLocaleDateString('fr-FR', { month: 'short' })}
          </text>);
        }
      }
    }
    return items;
  };

  // ===== BODY BACKGROUND (weekends, grid lines, month separators) =====
  const renderBodyBackground = (bodyH) => {
    const items = [];
    const start = new Date(timelineStart + 'T00:00:00');
    for (let i = 0; i < timelineDays; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      const x = i * dayWidth;
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      if (isWeekend && viewMode !== 'quarter') {
        items.push(<rect key={`bw-${i}`} x={x} y={0} width={dayWidth} height={bodyH} fill={COLORS.gridWeekend} opacity={viewMode === 'month' ? 0.5 : 1} />);
      }
      if (viewMode === 'month' && d.getDate() === 1) {
        items.push(<line key={`bm-${i}`} x1={x} y1={0} x2={x} y2={bodyH} stroke="#E0E0DB" strokeWidth={1} />);
      }
      if (viewMode === 'quarter' && d.getDate() === 1) {
        items.push(<line key={`bq-${i}`} x1={x} y1={0} x2={x} y2={bodyH} stroke="#E0E0DB" strokeWidth={0.5} />);
      }
    }
    return items;
  };

  // ===== DEPENDENCY ARROWS =====
  const renderDependencies = () => {
    const arrows = [];
    for (const task of visibleTaches) {
      for (const dep of (task.dependances || [])) {
        const pred = visibleTaches.find(t => t.id === dep.tache_id);
        if (!pred) continue;
        const predIdx = visibleTaches.indexOf(pred);
        const taskIdx = visibleTaches.indexOf(task);
        const predEndX = getBarX(pred.date_fin_calculee || pred.date_debut);
        const taskStartX = getBarX(task.date_debut);
        const predY = predIdx * ROW_H + ROW_H / 2;
        const taskY = taskIdx * ROW_H + ROW_H / 2;
        const midX = predEndX + 10;
        arrows.push(
          <g key={`dep-${pred.id}-${task.id}`}>
            <path
              d={`M${predEndX},${predY} L${midX},${predY} L${midX},${taskY} L${taskStartX - 4},${taskY}`}
              fill="none" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4,2"
            />
            <polygon
              points={`${taskStartX - 4},${taskY - 3} ${taskStartX},${taskY} ${taskStartX - 4},${taskY + 3}`}
              fill="#94A3B8"
            />
          </g>
        );
      }
    }
    return arrows;
  };

  // ===== RENDER BAR =====
  const renderTaskBar = (task, idx) => {
    const y = idx * ROW_H;
    const barY = y + 8;
    const barH = task.type === 'GROUPE' ? 8 : task.type === 'JALON' ? 0 : 20;
    const x = getBarX(task.date_debut);
    const w = task.type === 'JALON' ? 0 : getBarWidth(task.date_debut, task.date_fin_calculee);
    const isCritical = task.est_chemin_critique;
    const hasCRC = liens.some(l => l.tache_gantt_id === task.id);
    const retard = alertes.find(a => a.tache_id === task.id);
    const isCascaded = cascadedIds.has(task.id);

    // Cascade halo overlay
    const cascadeHalo = isCascaded ? (
      <rect
        x={task.type === 'JALON' ? x - 12 : x - 3}
        y={barY - 3}
        width={task.type === 'JALON' ? 24 : w + 6}
        height={(task.type === 'JALON' ? 24 : barH) + 6}
        fill="none"
        stroke="#F97316"
        strokeWidth={2.5}
        rx={5}
        className="cascade-flash-halo"
        style={{ pointerEvents: 'none' }}
      />
    ) : null;

    // Baseline bar
    let baselineBar = null;
    if (task.date_debut_baseline && task.duree_baseline) {
      const blX = getBarX(task.date_debut_baseline);
      const blEnd = addWorkDays(task.date_debut_baseline, task.duree_baseline);
      const blW = getBarWidth(task.date_debut_baseline, blEnd);
      if (task.type !== 'JALON' && (task.date_debut_baseline !== task.date_debut || task.duree_baseline !== task.duree_jours)) {
        baselineBar = <rect x={blX} y={barY + (task.type === 'GROUPE' ? 12 : 22)} width={blW} height={task.type === 'GROUPE' ? 4 : 6} fill={COLORS.baseline} rx={1} opacity={0.6} />;
      }
    }

    if (task.type === 'JALON') {
      return (
        <g key={task.id} data-testid={`gantt-bar-${task.id}`} opacity={task.ignored === true ? 0.4 : 1}>
          {cascadeHalo}
          <polygon
            points={`${x},${barY + 4} ${x + 8},${barY + 12} ${x},${barY + 20} ${x - 8},${barY + 12}`}
            fill={COLORS.milestone}
            stroke="#B8860B"
            strokeWidth={1}
            className="cursor-pointer"
            onDoubleClick={() => openTaskDetail(task)}
          />
          <text x={x + 14} y={barY + 16} className="fill-[#2C2C2C]" style={{ fontSize: 10, fontWeight: 500 }}>
            {task.nom}
          </text>
        </g>
      );
    }

    if (task.type === 'GROUPE') {
      return (
        <g key={task.id} data-testid={`gantt-bar-${task.id}`} opacity={task.ignored === true ? 0.4 : 1}>
          {cascadeHalo}
          {baselineBar}
          <rect x={x} y={barY + 4} width={w} height={barH} fill={COLORS.group} rx={0} opacity={0.7} />
          {/* Progress */}
          <rect x={x} y={barY + 4} width={w * (task.avancement_pct / 100)} height={barH} fill={COLORS.group} rx={0} />
          {/* End triangles */}
          <polygon points={`${x},${barY + 4} ${x},${barY + 16} ${x + 6},${barY + 4}`} fill={COLORS.group} />
          <polygon points={`${x + w},${barY + 4} ${x + w},${barY + 16} ${x + w - 6},${barY + 4}`} fill={COLORS.group} />
        </g>
      );
    }

    // Normal task bar
    const bgColor = isCritical ? COLORS.criticalBg : COLORS.progressBg;
    const fgColor = isCritical ? COLORS.critical : COLORS.progress;

    return (
      <g key={task.id} data-testid={`gantt-bar-${task.id}`} opacity={task.ignored === true ? 0.4 : 1}>
        {cascadeHalo}
        {baselineBar}
        {/* Background bar */}
        <rect x={x} y={barY} width={w} height={barH} fill={bgColor} rx={3}
          className={canEdit ? 'cursor-grab' : 'cursor-pointer'}
          onMouseDown={(e) => handleMouseDown(e, task, 'move')}
          onDoubleClick={() => openTaskDetail(task)}
        />
        {/* Progress fill */}
        <rect x={x} y={barY} width={w * (task.avancement_pct / 100)} height={barH} fill={fgColor} rx={3}
          style={{ pointerEvents: 'none' }}
        />
        {/* Resize handle */}
        {canEdit && (
          <rect x={x + w - 6} y={barY} width={6} height={barH} fill="transparent"
            className="cursor-ew-resize"
            onMouseDown={(e) => handleMouseDown(e, task, 'resize')}
          />
        )}
        {/* % label */}
        {w > 50 && (
          <text x={x + 6} y={barY + 14} className="fill-white" style={{ fontSize: 9, fontWeight: 600, pointerEvents: 'none' }}>
            {task.avancement_pct}%
          </text>
        )}
        {/* CRC icon */}
        {hasCRC && (
          <g transform={`translate(${x + w + 4}, ${barY + 2})`}>
            <circle cx={7} cy={7} r={7} fill="#D4A84B" />
            <text x={7} y={11} textAnchor="middle" fill="white" style={{ fontSize: 8, fontWeight: 700 }}>C</text>
          </g>
        )}
        {/* Retard badge */}
        {retard && (
          <text x={x + w + (hasCRC ? 20 : 4)} y={barY + 14} className="fill-[#C75B39]" style={{ fontSize: 9, fontWeight: 700 }}>
            +{retard.retard_jours}j
          </text>
        )}
      </g>
    );
  };

  // ===== NO PLANNING =====
  if (loading) return <div className="space-y-4 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>;

  if (!planning) {
    if (isClient) {
      return (
        <div className="text-center py-12" data-testid="planning-not-published">
          <CalendarBlank size={48} className="text-[#D4A84B] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Planning non disponible</h3>
          <p className="text-sm text-[#6B6B6B]">Le planning n'a pas encore été publié par l'architecte.</p>
        </div>
      );
    }
    return (
      <div className="text-center py-12" data-testid="planning-empty">
        <CalendarBlank size={48} className="text-[#D4A84B] mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucun planning</h3>
        <p className="text-sm text-[#6B6B6B] mb-6">Créez le planning de chantier pour suivre l'avancement.</p>
        {canEdit && (
          <Button onClick={handleCreatePlanning} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-2" data-testid="create-planning-button">
            <Plus size={16} weight="bold" /> Créer le planning
          </Button>
        )}
      </div>
    );
  }

  // ===== CLIENT VIEW =====
  if (isClient) {
    if (!planning.publie_client_le) {
      return (
        <div className="text-center py-12" data-testid="planning-not-published">
          <CalendarBlank size={48} className="text-[#D4A84B] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Planning en cours de préparation</h3>
          <p className="text-sm text-[#6B6B6B]">L'architecte n'a pas encore publié le planning.</p>
        </div>
      );
    }
    // Simplified client view: only groups + milestones
    const clientTasks = taches.filter(t => t.type === 'GROUPE' || t.type === 'JALON');
    const lateGroups = clientTasks.filter(t => t.type === 'GROUPE' && alertes.some(a => a.lot === t.nom));
    return (
      <div className="space-y-6" data-testid="planning-client-view">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Avancement du chantier</h3>
          <span className="text-xs text-[#6B6B6B]">Dernière mise à jour : {formatDateFR(planning.publie_client_le?.slice(0, 10))}</span>
        </div>
        {planning.message_client && (
          <div className="bg-[#F5F5F3] border border-[#E0E0DB] px-4 py-3 text-sm text-[#2C2C2C]">
            {planning.message_client}
          </div>
        )}
        {lateGroups.length > 0 && (
          <div className="bg-[#FFF3CD] border border-[#D4A84B] px-4 py-3">
            {lateGroups.map(g => {
              const retardMax = Math.max(...alertes.filter(a => a.lot === g.nom).map(a => a.retard_jours));
              return (
                <p key={g.id} className="text-sm text-[#6B6B6B]">
                  Le chantier a pris <strong className="text-[#C75B39]">{retardMax} jours</strong> de retard sur la phase <strong>{g.nom}</strong>.
                </p>
              );
            })}
          </div>
        )}
        <div className="space-y-3">
          {clientTasks.map(t => (
            <div key={t.id} className="border border-[#E0E0DB] px-4 py-3 flex items-center gap-4">
              {t.type === 'JALON' ? <Diamond size={20} className="text-[#D4A84B]" /> : <div className="w-5 h-5 rounded bg-[#1E3A5F]" />}
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2C2C2C]">{t.nom}</p>
                <p className="text-xs text-[#6B6B6B]">{formatDateFR(t.date_debut)} → {formatDateFR(t.date_fin_calculee)}</p>
              </div>
              {t.type !== 'JALON' && (
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-[#E0E0DB] rounded-full overflow-hidden">
                    <div className="h-full bg-[#1E3A5F] rounded-full" style={{ width: `${t.avancement_pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-[#2C2C2C]">{t.avancement_pct}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ===== FULL GANTT VIEW =====
  const groups = taches.filter(t => t.type === 'GROUPE' && !t.parent_id);

  return (
    <div data-testid="gantt-planning" className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* View mode toggle */}
        <div className="flex border border-[#E0E0DB]">
          {Object.entries(VIEW_MODES).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setViewMode(key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === key ? 'bg-[#1E3A5F] text-white' : 'text-[#6B6B6B] hover:bg-[#F5F5F3]'}`}
              data-testid={`view-mode-${key}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Iter 91 J3 — Toggle "Voir les ignorées" + compteur masquées */}
        {!showIgnored && ignoredCount > 0 && (
          <span
            className="text-xs text-[#6B6B6B]"
            data-testid="gantt-ignored-count"
          >
            {ignoredCount} ignorée{ignoredCount > 1 ? 's' : ''} masquée{ignoredCount > 1 ? 's' : ''}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="rounded-none gap-1 text-[#6B6B6B] hover:bg-[#F5F5F3]"
          onClick={() => setShowIgnored(v => !v)}
          aria-pressed={showIgnored}
          title={showIgnored ? 'Masquer les tâches ignorées' : 'Voir les tâches ignorées'}
          data-testid="gantt-toggle-ignored"
        >
          {showIgnored ? <Eye size={16} /> : <EyeSlash size={16} />}
          <span className="text-xs">Voir les ignorées</span>
        </Button>

        {/* Alerts CRC */}
        {alertes.length > 0 && (
          <Button variant="outline" size="sm" className="rounded-none gap-1 text-[#C75B39] border-[#C75B39]" onClick={() => setAlertesOpen(true)} data-testid="alertes-crc-button">
            <Warning size={14} />
            {alertes.length} tâche{alertes.length > 1 ? 's' : ''} en retard
          </Button>
        )}

        {/* RP41 / D7 — l'historique des plannings de référence est une LECTURE : ouvert
            à qui lit le Gantt (AMO, partenaire…), pas seulement à qui le modifie.
            Le client (MOA) n'y a pas accès (GET /planning/baselines refusé). */}
        {!isClient && (
          <Button variant="outline" size="sm" className="rounded-none gap-1" onClick={() => setVersionsOpen(true)} data-testid="planning-history-button">
            <ClockCounterClockwise size={14} /> Historique
          </Button>
        )}

        {canEdit && (
          <>
            <Button variant="outline" size="sm" className="rounded-none gap-1" onClick={() => setNewGroupOpen(true)} data-testid="add-group-button">
              <Plus size={14} /> Lot
            </Button>
            <Button variant="outline" size="sm" className="rounded-none gap-1" onClick={() => setNewTaskOpen(true)} data-testid="add-task-button">
              <Plus size={14} /> Tâche
            </Button>
            <Button variant="outline" size="sm" className="rounded-none gap-1" onClick={() => setBaselineOpen(true)} data-testid="save-baseline-button">
              <FloppyDisk size={14} /> Baseline
            </Button>
            <Button variant="outline" size="sm" className="rounded-none gap-1" onClick={() => setPublishOpen(true)} data-testid="publish-button">
              <Eye size={14} /> Publier client
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none gap-1"
              onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/planning/export-csv`, '_blank')}
              data-testid="export-csv-button"
            >
              <Export size={14} /> CSV
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none gap-1"
              onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/planning/export-pdf`, '_blank')}
              data-testid="export-pdf-button"
            >
              <Export size={14} /> PDF
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none gap-1"
              onClick={() => importInputRef.current?.click()}
              data-testid="import-excel-button"
            >
              <Plus size={14} /> Importer Excel
            </Button>
            <Button
              size="sm"
              className="bg-[#C4773B] hover:bg-[#A85F25] text-white rounded-none gap-1"
              onClick={() => setShowIAGenerate(true)}
              data-testid="generate-ia-planning-btn"
            >
              <Sparkle size={14} weight="fill" /> Générer par IA
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none gap-1"
              onClick={() => {
                if (selectedTaskIds.size === taches.length) {
                  setSelectedTaskIds(new Set());
                } else {
                  setSelectedTaskIds(new Set(taches.map(t => t.id)));
                }
              }}
              data-testid="select-all-planning-button"
            >
              {selectedTaskIds.size === taches.length && taches.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
          </>
        )}
      </div>

      {/* Baselines info */}
      {planning.baselines?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
          <span className="inline-block w-3 h-2 bg-[#D4D4D4] rounded-sm" />
          Baseline : {planning.baselines[planning.baselines.length - 1].label}
          ({formatDateFR(planning.baselines[planning.baselines.length - 1].date?.slice(0, 10))})
          {planning.baselines.length > 1 && <span> — {planning.baselines.length} versions</span>}
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedTaskIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-[#1E3A5F] text-white mb-2" data-testid="gantt-bulk-actions">
          <span className="text-sm font-semibold flex-1">{selectedTaskIds.size} sélectionnée(s)</span>
          <Button size="sm" variant="ghost" className="text-[#FFCDD2] hover:bg-white/20 rounded-none gap-1 text-xs" data-testid="gantt-bulk-delete" onClick={async () => {
            if (!window.confirm(`Supprimer ${selectedTaskIds.size} tâche(s) ?`)) return;
            try {
              await Promise.all([...selectedTaskIds].map(id => axios.delete(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${id}`, { withCredentials: true })));
              toast.success(`${selectedTaskIds.size} tâche(s) supprimée(s)`);
              setSelectedTaskIds(new Set());
              fetchPlanning();
            } catch { toast.error('Erreur'); }
          }}>
            <Trash size={14} /> Supprimer
          </Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 rounded-none" onClick={() => setSelectedTaskIds(new Set())}><X size={14} /></Button>
        </div>
      )}

      {/* GANTT CHART — single scroll container with CSS Grid + sticky header/left column */}
      <div
        ref={scrollRef}
        className="border border-[#E0E0DB] bg-white relative"
        style={{ maxHeight: 600, overflow: 'auto' }}
        data-testid="gantt-scroll-container"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${LEFT_W}px ${totalWidth}px`,
            gridTemplateRows: `${HEADER_H}px auto`,
            width: LEFT_W + totalWidth,
          }}
        >
          {/* 1. Left header (sticky top+left) */}
          <div
            className="bg-[#F5F5F3] border-b border-r border-[#E0E0DB] flex items-center"
            style={{ position: 'sticky', top: 0, left: 0, zIndex: 30, height: HEADER_H }}
            data-testid="gantt-left-header"
          >
            <span style={{ width: COL_CHECK }} />
            <span className="text-xs font-bold text-[#2C2C2C] uppercase tracking-wider px-2" style={{ width: COL_NAME }}>Tâche</span>
            <span className="text-xs font-bold text-[#2C2C2C] uppercase tracking-wider px-2" style={{ width: COL_LOT }}>Lot</span>
            <span className="text-xs font-bold text-[#2C2C2C] uppercase tracking-wider px-2" style={{ width: COL_ENT }}>Entreprise</span>
            <span className="text-xs font-bold text-[#2C2C2C] uppercase tracking-wider text-center" style={{ width: COL_DUR }}>Durée</span>
            <span className="text-xs font-bold text-[#2C2C2C] uppercase tracking-wider text-center" style={{ width: COL_PCT }}>Avancement</span>
            <span style={{ width: COL_ACTIONS }} />
          </div>

          {/* 2. Right header (sticky top) — timeline dates */}
          <div
            className="bg-[#F5F5F3] border-b border-[#E0E0DB]"
            style={{ position: 'sticky', top: 0, zIndex: 20, height: HEADER_H }}
            data-testid="gantt-timeline-header"
          >
            <svg width={totalWidth} height={HEADER_H} className="select-none block">
              <rect x={0} y={0} width={totalWidth} height={HEADER_H} fill="#F5F5F3" />
              {renderTimelineHeader()}
              {/* Today badge in header */}
              <rect x={todayOffset - 24} y={2} width={48} height={16} fill={COLORS.today} rx={2} />
              <text x={todayOffset} y={14} textAnchor="middle" fill="white" style={{ fontSize: 8, fontWeight: 700 }}>Aujourd&apos;hui</text>
            </svg>
          </div>

          {/* 3. Left body (sticky left) — task rows */}
          <div
            className="bg-white border-r border-[#E0E0DB]"
            style={{ position: 'sticky', left: 0, zIndex: 10 }}
            data-testid="gantt-left-body"
          >
            {visibleTaches.map((task) => {
              const isGroup = task.type === 'GROUPE';
              const isJalon = task.type === 'JALON';
              const isChild = !!task.parent_id;
              const lotMarche = lotsMarches.find(l => l.code === task.lot_marche_code);
              return (
                <div
                  key={task.id}
                  className={`flex items-center border-b border-[#F0F0EE] hover:bg-[#FAFAF9] transition-colors group ${isGroup ? 'bg-[#F5F5F3]' : ''} ${selectedTaskIds.has(task.id) ? 'bg-[#EFF6FF]' : ''}`}
                  style={{ height: ROW_H, opacity: task.ignored === true ? 0.4 : 1 }}
                  data-testid={`gantt-row-${task.id}`}
                >
                  {/* Checkbox */}
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: COL_CHECK }}>
                    {canEdit && (
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={() => setSelectedTaskIds(prev => { const n = new Set(prev); if (n.has(task.id)) n.delete(task.id); else n.add(task.id); return n; })}
                        className="accent-[#1E3A5F]"
                        style={{ width: 14, height: 14 }}
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                  </div>
                  {/* Name (clickable → opens detail panel) */}
                  <div
                    className="flex items-center gap-1 px-2 min-w-0 cursor-pointer"
                    style={{ width: COL_NAME }}
                    onClick={() => openTaskDetail(task)}
                    data-testid={`gantt-row-name-${task.id}`}
                  >
                    {isGroup && (
                      <button onClick={(e) => { e.stopPropagation(); setCollapsed(prev => ({ ...prev, [task.id]: !prev[task.id] })); }} className="p-0.5 hover:bg-[#E0E0DB] rounded flex-shrink-0" data-testid={`collapse-${task.id}`}>
                        {collapsed[task.id] ? <CaretRight size={12} /> : <CaretDown size={12} />}
                      </button>
                    )}
                    {isChild && <span className="w-4 flex-shrink-0" />}
                    {isJalon && <Diamond size={12} className="text-[#D4A84B] flex-shrink-0" />}
                    <span
                      className={`text-xs truncate ${isGroup ? 'font-bold text-[#2C2C2C]' : 'text-[#4A4A4A] hover:text-[#1E3A5F]'}`}
                      style={task.ignored === true ? { textDecoration: 'line-through' } : undefined}
                      title={task.nom}
                    >
                      {task.nom}
                    </span>
                  </div>
                  {/* Lot (Select) */}
                  <div className="px-1 flex-shrink-0" style={{ width: COL_LOT }} onClick={e => e.stopPropagation()}>
                    {!isGroup && canEdit ? (
                      <Select
                        value={task.lot_marche_code || 'none'}
                        onValueChange={v => {
                          const sel = lotsMarches.find(l => l.code === v);
                          const patch = { lot_marche_code: v === 'none' ? '' : v };
                          if (sel?.entreprise_nom && !task.entreprise_nom) patch.entreprise_nom = sel.entreprise_nom;
                          patchTask(task.id, patch);
                        }}
                      >
                        <SelectTrigger className="h-7 text-[11px] rounded-none px-2" data-testid={`row-lot-${task.id}`}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {lotsMarches.filter(l => l.code).map(l => <SelectItem key={l.code} value={l.code} className="text-xs">{l.code} — {l.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[10px] text-[#6B6B6B] truncate block">{task.lot_marche_code || ''}</span>
                    )}
                  </div>
                  {/* Entreprise (Input, pre-filled) */}
                  <div className="px-1 flex-shrink-0" style={{ width: COL_ENT }} onClick={e => e.stopPropagation()}>
                    {!isGroup && canEdit ? (
                      <Input
                        defaultValue={task.entreprise_nom || lotMarche?.entreprise_nom || ''}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (task.entreprise_nom || '')) patchTask(task.id, { entreprise_nom: v });
                        }}
                        placeholder={lotMarche?.entreprise_nom || '—'}
                        className="h-7 text-[11px] rounded-none px-2"
                        data-testid={`row-entreprise-${task.id}`}
                      />
                    ) : (
                      <span className="text-[10px] text-[#6B6B6B] truncate block">{task.entreprise_nom || ''}</span>
                    )}
                  </div>
                  {/* Durée */}
                  <div className="text-center flex-shrink-0" style={{ width: COL_DUR }}>
                    <span className="text-[10px] text-[#6B6B6B]">{isJalon ? '—' : `${task.duree_jours}j`}</span>
                  </div>
                  {/* Avancement (bar + %) */}
                  <div className="px-1 flex-shrink-0 flex items-center gap-1" style={{ width: COL_PCT }} onClick={e => e.stopPropagation()}>
                    {!isGroup && !isJalon ? (
                      <>
                        <div className="flex-1 h-1.5 bg-[#E0E0DB] rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${task.avancement_pct === 100 ? 'bg-[#2D6A4F]' : task.est_chemin_critique ? 'bg-[#C75B39]' : 'bg-[#1E3A5F]'}`}
                            style={{ width: `${task.avancement_pct}%` }}
                          />
                        </div>
                        {canEdit ? (
                          <input
                            type="number"
                            min={0}
                            max={100}
                            defaultValue={task.avancement_pct}
                            onBlur={(e) => {
                              const v = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                              if (v !== task.avancement_pct) patchTask(task.id, { avancement_pct: v });
                            }}
                            className="w-8 text-[10px] font-medium text-right bg-transparent border-0 outline-none focus:bg-white focus:border focus:border-[#1E3A5F] p-0.5"
                            data-testid={`row-avancement-${task.id}`}
                          />
                        ) : (
                          <span className="text-[10px] font-medium w-8 text-right">{task.avancement_pct}%</span>
                        )}
                      </>
                    ) : (
                      <span className="text-[10px] text-[#6B6B6B] flex-1 text-center">{isGroup ? `${task.avancement_pct || 0}%` : ''}</span>
                    )}
                  </div>
                  {/* Delete */}
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: COL_ACTIONS }}>
                    {canEdit && (
                      <button
                        className="w-5 h-5 flex items-center justify-center text-[#9A9A9A] hover:text-[#8B3A3A] opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.delete(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${task.id}`, { withCredentials: true });
                            toast.success('Tâche supprimée');
                            fetchPlanning();
                          } catch { toast.error('Erreur'); }
                        }}
                        title="Supprimer"
                        data-testid={`gantt-delete-${task.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 4. Right body — SVG with bars, deps, weekends, today line */}
          <div data-testid="gantt-right-body">
            <svg
              ref={svgRef}
              width={totalWidth}
              height={Math.max(visibleTaches.length * ROW_H + 20, 120)}
              className="select-none block"
            >
              {/* Weekend stripes + month separators */}
              {renderBodyBackground(visibleTaches.length * ROW_H + 20)}
              {/* Horizontal grid lines */}
              {visibleTaches.map((_, idx) => (
                <line key={`gl-${idx}`} x1={0} y1={(idx + 1) * ROW_H} x2={totalWidth} y2={(idx + 1) * ROW_H} stroke={COLORS.gridLine} strokeWidth={0.5} />
              ))}
              {/* Today vertical line */}
              <line x1={todayOffset} y1={0} x2={todayOffset} y2={visibleTaches.length * ROW_H + 20} stroke={COLORS.today} strokeWidth={2} strokeDasharray="6,3" />
              {/* Dependencies */}
              {renderDependencies()}
              {/* Task bars */}
              {visibleTaches.map((task, idx) => renderTaskBar(task, idx))}
            </svg>
          </div>
        </div>
      </div>

      {/* EDIT TASK DIALOG (fallback for internal edits) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
              {editTask?.type === 'JALON' ? 'Modifier le jalon' : editTask?.type === 'GROUPE' ? 'Modifier le lot' : 'Modifier la tâche'}
            </DialogTitle>
          </DialogHeader>
          {editTask && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Intitulé</Label>
                <Input value={editTask.nom} onChange={e => setEditTask(p => ({ ...p, nom: e.target.value }))} className="rounded-none mt-1" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">Annuler</Button>
                <Button onClick={() => handleSaveTask(editTask)} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-2">
                  <FloppyDisk size={14} /> Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== TASK DETAIL SHEET (slide-over from right) ===== */}
      <Sheet open={!!detailTaskId} onOpenChange={(v) => { if (!v) { setDetailTaskId(null); setDetailTask(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-white" data-testid="gantt-detail-sheet">
          <SheetHeader>
            <SheetTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
              {detailTask?.type === 'JALON' ? 'Détail du jalon' : detailTask?.type === 'GROUPE' ? 'Détail du lot' : 'Détail de la tâche'}
            </SheetTitle>
          </SheetHeader>
          {detailTask && (
            <div className="space-y-4 mt-4">
              {detailTask.decale_par && (
                <div
                  data-testid="detail-decalage-auto"
                  className="flex items-start gap-2 bg-[#FFF4E5] border-l-4 border-[#F97316] px-3 py-2 text-xs text-[#7A3B0A]"
                >
                  <Warning size={16} weight="fill" className="text-[#F97316] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">Décalée automatiquement</div>
                    <div className="mt-0.5">
                      {detailTask.decale_le ? `Le ${formatDateFR(detailTask.decale_le.slice(0, 10))} ` : ''}
                      suite à «&nbsp;<span className="font-medium">{
                        detailTask.decale_par_nom ||
                        taches.find(t => t.id === detailTask.decale_par)?.nom ||
                        detailTask.decale_par
                      }</span>&nbsp;»
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B6B6B]">Nom</Label>
                <Input
                  value={detailTask.nom}
                  onChange={e => setDetailTask(p => ({ ...p, nom: e.target.value }))}
                  disabled={!canEdit}
                  className="rounded-none mt-1"
                  data-testid="detail-nom"
                />
              </div>
              {detailTask.type !== 'GROUPE' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#6B6B6B]">Lot / Marché</Label>
                    <Select
                      value={detailTask.lot_marche_code || 'none'}
                      disabled={!canEdit}
                      onValueChange={v => {
                        const sel = lotsMarches.find(l => l.code === v);
                        setDetailTask(p => ({
                          ...p,
                          lot_marche_code: v === 'none' ? '' : v,
                          entreprise_nom: sel?.entreprise_nom || p.entreprise_nom || '',
                        }));
                      }}
                    >
                      <SelectTrigger className="rounded-none mt-1" data-testid="detail-lot"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun lot</SelectItem>
                        {lotsMarches.filter(l => l.code).map(l => <SelectItem key={l.code} value={l.code}>{l.code} — {l.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B6B6B]">Entreprise</Label>
                    <Input
                      value={detailTask.entreprise_nom || ''}
                      onChange={e => setDetailTask(p => ({ ...p, entreprise_nom: e.target.value }))}
                      disabled={!canEdit}
                      className="rounded-none mt-1"
                      data-testid="detail-entreprise"
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Date début</Label>
                  <Input
                    type="date"
                    value={detailTask.date_debut || ''}
                    onChange={e => setDetailTask(p => ({ ...p, date_debut: e.target.value }))}
                    disabled={!canEdit}
                    className="rounded-none mt-1"
                    data-testid="detail-date-debut"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Durée (j. ouv.)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={detailTask.duree_jours}
                    onChange={e => setDetailTask(p => ({ ...p, duree_jours: parseInt(e.target.value) || 1 }))}
                    disabled={!canEdit || detailTask.type === 'JALON'}
                    className="rounded-none mt-1"
                    data-testid="detail-duree"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Avancement %</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={detailTask.avancement_pct}
                    onChange={e => setDetailTask(p => ({ ...p, avancement_pct: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) }))}
                    disabled={!canEdit}
                    className="rounded-none mt-1"
                    data-testid="detail-avancement"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Date de fin calculée</Label>
                <p className="text-sm font-medium text-[#2C2C2C] mt-1">{formatDateFR(addWorkDays(detailTask.date_debut, detailTask.duree_jours))}</p>
              </div>
              {detailTask.type !== 'GROUPE' && (
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Dépendances (prédécesseurs)</Label>
                  <div className="mt-1 max-h-40 overflow-y-auto border border-[#E0E0DB] p-2 space-y-1 bg-[#FAFAF9]">
                    {taches.filter(t => t.id !== detailTask.id && t.type !== 'GROUPE').length === 0 ? (
                      <p className="text-xs text-[#9A9A9A] italic">Aucune autre tâche disponible</p>
                    ) : (
                      taches.filter(t => t.id !== detailTask.id && t.type !== 'GROUPE').map(t => {
                        const deps = detailTask.dependances || [];
                        const checked = deps.some(d => d.tache_id === t.id);
                        return (
                          <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white px-1 py-0.5" data-testid={`detail-dep-${t.id}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!canEdit}
                              onChange={(e) => {
                                setDetailTask(p => {
                                  const cur = p.dependances || [];
                                  const next = e.target.checked
                                    ? [...cur, { tache_id: t.id, type: 'FS', decalage_jours: 0 }]
                                    : cur.filter(d => d.tache_id !== t.id);
                                  return { ...p, dependances: next };
                                });
                              }}
                              className="accent-[#1E3A5F]"
                            />
                            <span className={`truncate flex-1 ${t.type === 'JALON' ? 'italic text-[#D4A84B]' : ''}`}>{t.nom}</span>
                            <span className="text-[10px] text-[#9A9A9A]">{formatDateFR(t.date_debut)}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B6B6B]">Notes</Label>
                <Textarea
                  value={detailTask.notes || ''}
                  onChange={e => setDetailTask(p => ({ ...p, notes: e.target.value }))}
                  disabled={!canEdit}
                  rows={3}
                  className="rounded-none mt-1"
                  data-testid="detail-notes"
                />
              </div>
              {canEdit && (
                <div className="flex justify-between pt-4 border-t border-[#E0E0DB]">
                  <Button
                    variant="outline"
                    className="rounded-none gap-1 text-[#8B3A3A] border-[#8B3A3A] hover:bg-[#FFF5F5]"
                    onClick={async () => {
                      if (!window.confirm(`Supprimer "${detailTask.nom}" ?`)) return;
                      await handleDeleteTask(detailTask.id);
                      setDetailTaskId(null);
                      setDetailTask(null);
                    }}
                    data-testid="detail-delete"
                  >
                    <Trash size={14} /> Supprimer
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setDetailTaskId(null); setDetailTask(null); }} className="rounded-none">Annuler</Button>
                    <Button
                      onClick={async () => {
                        await handleSaveTask(detailTask);
                        setDetailTaskId(null);
                        setDetailTask(null);
                      }}
                      className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-2"
                      data-testid="detail-save"
                    >
                      <FloppyDisk size={14} /> Enregistrer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ADD GROUP DIALOG */}
      <Dialog open={newGroupOpen} onOpenChange={setNewGroupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter un lot</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Nom du lot</Label>
              <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} className="rounded-none mt-1" placeholder="Ex: Plomberie" data-testid="new-group-name" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewGroupOpen(false)} className="rounded-none">Annuler</Button>
              <Button onClick={handleAddGroup} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none" data-testid="save-group-button">Créer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD TASK DIALOG */}
      <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter une tâche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Lot parent</Label>
              <Select value={newTaskParent} onValueChange={setNewTaskParent}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Sélectionner un lot" /></SelectTrigger>
                <SelectContent>
                  {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Intitulé</Label>
                <Input value={newTaskData.nom} onChange={e => setNewTaskData(p => ({ ...p, nom: e.target.value }))} className="rounded-none mt-1" data-testid="new-task-nom" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Type</Label>
                <Select value={newTaskData.type} onValueChange={v => setNewTaskData(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TACHE">Tâche</SelectItem>
                    <SelectItem value="JALON">Jalon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Date début</Label>
                <Input type="date" value={newTaskData.date_debut} onChange={e => setNewTaskData(p => ({ ...p, date_debut: e.target.value }))} className="rounded-none mt-1" data-testid="new-task-date" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Durée (jours ouv.)</Label>
                <Input type="number" min={1} value={newTaskData.duree_jours} onChange={e => setNewTaskData(p => ({ ...p, duree_jours: parseInt(e.target.value) || 1 }))} className="rounded-none mt-1" data-testid="new-task-duree" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Lot / Marché</Label>
              <Select value={newTaskData.lot_marche_code || 'none'} onValueChange={v => {
                const sel = lotsMarches.find(l => l.code === v);
                setNewTaskData(p => ({ ...p, lot_marche_code: v === 'none' ? '' : v, entreprise_nom: sel?.entreprise_nom || p.entreprise_nom || '' }));
              }}>
                <SelectTrigger className="rounded-none mt-1" data-testid="new-task-lot-marche"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun lot</SelectItem>
                  {lotsMarches.filter(l => l.code).map(l => <SelectItem key={l.code} value={l.code}>{l.code} — {l.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Entreprise</Label>
              <Input value={newTaskData.entreprise_nom} onChange={e => setNewTaskData(p => ({ ...p, entreprise_nom: e.target.value }))} className="rounded-none mt-1" placeholder="Nom de l'entreprise" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setNewTaskOpen(false)} className="rounded-none">Annuler</Button>
              <Button onClick={handleAddTask} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none" data-testid="save-new-task-button">Créer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* BASELINE DIALOG */}
      <Dialog open={baselineOpen} onOpenChange={setBaselineOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Sauvegarder le planning de référence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Label de la baseline</Label>
              <Input value={baselineLabel} onChange={e => setBaselineLabel(e.target.value)} className="rounded-none mt-1" placeholder="Ex: Planning initial signé" data-testid="baseline-label-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBaselineOpen(false)} className="rounded-none">Annuler</Button>
              <Button onClick={handleSaveBaseline} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none" data-testid="save-baseline-confirm">Sauvegarder</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PUBLISH DIALOG */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Publier vers le client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#6B6B6B]">Le client verra une vue simplifiée avec les grandes phases et jalons uniquement.</p>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Message pour le client (optionnel)</Label>
              <Textarea value={publishMsg} onChange={e => setPublishMsg(e.target.value)} rows={3} className="rounded-none mt-1" placeholder="Ex: Le chantier avance bien, quelques jours de retard sur le gros oeuvre..." data-testid="publish-message" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPublishOpen(false)} className="rounded-none">Annuler</Button>
              <Button onClick={handlePublish} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none" data-testid="publish-confirm">Publier</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ALERTES CRC DIALOG */}
      <Dialog open={alertesOpen} onOpenChange={setAlertesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Tâches en retard</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {alertes.map(a => (
              <div key={a.tache_id} className="flex items-center gap-3 border border-[#E0E0DB] px-4 py-3">
                <Warning size={18} className="text-[#C75B39]" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#2C2C2C]">{a.nom}</p>
                  <p className="text-xs text-[#6B6B6B]">{a.lot} — {a.entreprise_nom}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#C75B39]">+{a.retard_jours}j</p>
                  <p className="text-xs text-[#6B6B6B]">{a.avancement_pct}% fait</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <input ref={importInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportExcel} style={{ display: 'none' }} data-testid="import-excel-input" />
      {showIAGenerate && (
        <GeneratePlanningIADialog
          chantierId={chantierId}
          onClose={() => setShowIAGenerate(false)}
          onImported={() => { fetchPlanning(); setShowIAGenerate(false); }}
        />
      )}

      {/* REFONTE Session 5.4 — Swimlane Éléments terrain */}
      <ElementsTerrainSwimlane chantierId={chantierId} />

      {/* PATCH v6 Sprint 3 — Historique des baselines */}
      <PlanningVersionsModal
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        chantierId={chantierId}
      />
    </div>
  );
}
